import type { Customer } from '@features/customers/types/customers.types';
import { NO_LINK_DELIVERY, type CustomerLinkContacts, type LinkDeliveryChoice } from '../types/linkDelivery.types';
import type { CollectionRouting, DealerCollectedDeposit, PaymentSelection } from '../types/schedule.types';

export type PaymentCollectionAxis = 'deposit' | 'full_payment';

/** The terminal action a confirmed routing leads to. */
export type PaymentCollectionAction = 'checkout' | 'complete';

const PAID_IN_FULL_METHOD_KEY = 'paid_in_full';

const isPositiveInteger = (value: unknown): value is number =>
    typeof value === 'number' && Number.isInteger(value) && value > 0;

/** The axis this confirmed selection collects on, or null when there is no up-front
 *  collection to route (a scheduled sale with no deposit). */
export const paymentCollectionAxis = (
    selection: PaymentSelection | undefined,
): PaymentCollectionAxis | null => {
    if (!selection) {
        return null;
    }
    if (selection.methodKey === PAID_IN_FULL_METHOD_KEY) {
        return 'full_payment';
    }
    return selection.params.hasDeposit === true ? 'deposit' : null;
};

/** The amount the chosen collector takes now: the deposit on the deposit axis, the whole
 *  sale on the full-payment axis. Undefined when it cannot be read as a positive integer. */
export const paymentCollectionAmountMinor = (
    selection: PaymentSelection | undefined,
    axis: PaymentCollectionAxis,
): number | undefined => {
    if (!selection) {
        return undefined;
    }
    const value = axis === 'deposit' ? selection.params.depositAmount : selection.params.amount;
    return isPositiveInteger(value) ? value : undefined;
};

export type PaymentCollectionModalMode =
    | { kind: 'choice'; axis: PaymentCollectionAxis; amountMinor: number }
    | { kind: 'dealer_only'; axis: 'full_payment'; amountMinor: number }
    | { kind: 'motorplan_only'; axis: PaymentCollectionAxis | null }
    | { kind: 'none' };

const NO_MODAL: PaymentCollectionModalMode = { kind: 'none' };

/** What the post-summary modal offers: a who-collects choice, a dealer-only receipt form, a
 *  MotorPlan-only note, or nothing when the caller can neither initiate nor collect. */
export const paymentCollectionModalMode = (
    selection: PaymentSelection | undefined,
    canSelfCollect: boolean,
    canInitiate: boolean,
): PaymentCollectionModalMode => {
    const axis = paymentCollectionAxis(selection);
    const amountMinor = axis === null ? undefined : paymentCollectionAmountMinor(selection, axis);
    if (axis !== null && amountMinor !== undefined && canSelfCollect) {
        if (canInitiate) {
            return { kind: 'choice', axis, amountMinor };
        }
        return axis === 'full_payment' ? { kind: 'dealer_only', axis, amountMinor } : NO_MODAL;
    }
    if (!canInitiate) {
        return NO_MODAL;
    }
    return { kind: 'motorplan_only', axis: axis === 'full_payment' || amountMinor !== undefined ? axis : null };
};

/** Whether the send-link checkboxes render: only where the confirmed choice creates an
 *  application, and never while a held checkout would be reopened locally with no request. */
export const offersLinkDelivery = (
    mode: PaymentCollectionModalMode,
    collectedBy: 'elemis' | 'dealer',
    liveCheckoutHeld: boolean,
): boolean => {
    if (liveCheckoutHeld) {
        return false;
    }
    switch (mode.kind) {
        case 'motorplan_only':
            return true;
        case 'choice':
            return mode.axis === 'deposit' || collectedBy === 'elemis';
        case 'dealer_only':
        case 'none':
            return false;
    }
};

/** The full-payment axis must persist the explicit 'elemis' (absence is the legacy born-Active
 *  sale); the deposit axis omits it, where absence IS Elemis Direct Debit. */
export const elemisRoutingFor = (axis: PaymentCollectionAxis): CollectionRouting | undefined =>
    axis === 'full_payment' ? { fullPaymentCollection: 'elemis' } : undefined;

/** Routing for a dealer-collected choice, carrying the receipt. */
export const dealerRoutingFor = (
    axis: PaymentCollectionAxis,
    dealerPayment: DealerCollectedDeposit,
): CollectionRouting =>
    axis === 'deposit'
        ? { depositCollection: 'dealer', dealerPayment }
        : { fullPaymentCollection: 'dealer', dealerPayment };

/** Replaces the selection's collection routing, dropping the key entirely when undefined so a
 *  deposit-axis Elemis choice persists as the absence the wire expects. */
export const applyCollectionRouting = (
    selection: PaymentSelection,
    routing: CollectionRouting | undefined,
): PaymentSelection => {
    if (routing === undefined) {
        const next = { ...selection };
        delete next.collectionRouting;
        return next;
    }
    return { ...selection, collectionRouting: routing };
};

/** A dealer-collected paid-in-full sale completes with no Elemis application (born Active);
 *  every other routing initiates the Elemis checkout. */
export const actionForRouting = (
    axis: PaymentCollectionAxis,
    routing: CollectionRouting | undefined,
): PaymentCollectionAction =>
    axis === 'full_payment' && routing?.fullPaymentCollection === 'dealer' ? 'complete' : 'checkout';

/** Ticked where the contact exists. */
export const defaultLinkDelivery = (contacts: CustomerLinkContacts): LinkDeliveryChoice => ({
    sendViaEmail: contacts.email !== undefined,
    sendViaSms: contacts.mobile !== undefined,
});

const trimmedOrUndefined = (value: string | undefined): string | undefined => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
};

export const customerLinkContacts = (
    customer: Partial<Pick<Customer, 'email' | 'mobile_phone'>> | null | undefined,
): CustomerLinkContacts => ({
    email: trimmedOrUndefined(customer?.email),
    mobile: trimmedOrUndefined(customer?.mobile_phone),
});

/** The delivery a confirmed modal sends: the ticked boxes where the contact exists, and
 *  nothing at all when the checkboxes were not offered. */
export const confirmedLinkDelivery = (
    offered: boolean,
    ticked: LinkDeliveryChoice,
    contacts: CustomerLinkContacts,
): LinkDeliveryChoice =>
    offered
        ? {
              sendViaEmail: ticked.sendViaEmail && contacts.email !== undefined,
              sendViaSms: ticked.sendViaSms && contacts.mobile !== undefined,
          }
        : NO_LINK_DELIVERY;
